/**
 * Livre d'or vocal : messages audio enregistrés par les invités, persistés
 * dans IndexedDB.
 */
import { defineStore } from "pinia";
import { ref } from "vue";

import type { MomentId, VoiceNote } from "@/types/album";

import { deleteVoice, getAllVoice, isQuotaError, putVoice } from "@/lib/db";
import { createId } from "@/lib/id";
import { useNotificationsStore } from "@/stores/notifications";

const STORAGE_UNAVAILABLE_MESSAGE =
  "Le stockage local n'est pas disponible : les messages vocaux resteront en mémoire pour cette visite.";
const QUOTA_EXCEEDED_MESSAGE =
  "Mémoire de stockage pleine : ce message vocal n'a pas pu être enregistré durablement.";

export const useGuestbookStore = defineStore("guestbook", () => {
  const notes = ref<VoiceNote[]>([]);
  const isReady = ref(false);
  const objectUrls = new Map<string, string>();

  async function hydrate(): Promise<void> {
    const notifications = useNotificationsStore();
    try {
      const stored = await getAllVoice();
      notes.value = [...stored].sort((a, b) => b.createdAt - a.createdAt);
    } catch {
      notifications.notify(STORAGE_UNAVAILABLE_MESSAGE, "error");
    } finally {
      isReady.value = true;
    }
  }

  async function addNote(input: {
    blob: Blob;
    durationMs: number;
    author: string;
    moment?: MomentId | undefined;
  }): Promise<VoiceNote> {
    const note: VoiceNote = {
      id: createId("voice"),
      createdAt: Date.now(),
      durationMs: input.durationMs,
      author: input.author,
      moment: input.moment,
      blob: input.blob,
    };

    const notifications = useNotificationsStore();
    try {
      await putVoice(note);
    } catch (error) {
      notifications.notify(
        isQuotaError(error) ? QUOTA_EXCEEDED_MESSAGE : STORAGE_UNAVAILABLE_MESSAGE,
        "error",
      );
    }

    notes.value = [note, ...notes.value];
    return note;
  }

  function revoke(id: string): void {
    const url = objectUrls.get(id);
    if (url) {
      URL.revokeObjectURL(url);
      objectUrls.delete(id);
    }
  }

  async function removeNote(id: string): Promise<void> {
    const exists = notes.value.some((note) => note.id === id);
    if (!exists) return;

    try {
      await deleteVoice(id);
    } catch {
      // Le stockage peut être indisponible : on retire quand même la note affichée.
    }

    notes.value = notes.value.filter((note) => note.id !== id);
    revoke(id);
  }

  function urlFor(id: string): string | undefined {
    const existingUrl = objectUrls.get(id);
    if (existingUrl) return existingUrl;

    const note = notes.value.find((candidate) => candidate.id === id);
    if (!note) return undefined;

    const url = URL.createObjectURL(note.blob);
    objectUrls.set(id, url);
    return url;
  }

  function disposeAll(): void {
    objectUrls.forEach((url) => URL.revokeObjectURL(url));
    objectUrls.clear();
  }

  return { notes, isReady, hydrate, addNote, removeNote, urlFor, disposeAll };
});
